import { Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { deleteCalendarPreview } from '@/lib/calendars';

/**
 * Cascade-delete confirmation for one calendar. `usage` is the freshly fetched
 * `{slots, sessionBlocks, upcomingAppointments}` (falls back to the calendar's own counts while
 * loading or when the refresh failed). Confirm stays disabled until the counts are in, so the
 * person never agrees to a cascade they have not seen.
 */
export function DeleteCalendarDialog({
  open,
  onOpenChange,
  calendar,
  usage = null,
  loadingUsage = false,
  deleting = false,
  onConfirm,
}) {
  const name = String(calendar?.name || '').trim() || 'this calendar';
  const preview = deleteCalendarPreview(calendar, usage);
  const busy = loadingUsage || deleting;

  return (
    <Dialog open={open} onOpenChange={(next) => (deleting ? null : onOpenChange(next))}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-md">
        <div className="border-b border-border/60 bg-muted/30 px-6 pb-4 pt-6">
          <DialogHeader className="space-y-4 text-left">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/15 text-destructive ring-1 ring-destructive/20 dark:bg-destructive/10 dark:ring-destructive/25"
              aria-hidden
            >
              <Trash2 className="h-6 w-6" strokeWidth={2.25} />
            </div>
            <div className="space-y-1.5">
              <DialogTitle className="text-xl font-semibold tracking-tight">Delete {name}?</DialogTitle>
              <DialogDescription className="text-[14px] leading-relaxed text-muted-foreground">
                The calendar disappears from every picker and column. This cannot be undone here.
              </DialogDescription>
            </div>
          </DialogHeader>
        </div>
        <div className="space-y-2.5 px-6 py-4 text-[13.5px]">
          <div className="flex items-start gap-2">
            <span className="w-28 shrink-0 font-bold text-destructive">Will be removed</span>
            <span className="text-foreground">
              {loadingUsage ? (
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  Counting…
                </span>
              ) : (
                preview.removed
              )}
            </span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-28 shrink-0 font-bold text-muted-foreground">Kept</span>
            <span className="text-foreground">
              {loadingUsage ? '…' : preview.kept}
              <span className="text-muted-foreground"> — they keep pointing at the deleted calendar.</span>
            </span>
          </div>
          {preview.cancelsDirectEntries ? (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900 ring-1 ring-inset ring-amber-300/70 dark:bg-amber-950/50 dark:text-amber-100 dark:ring-amber-500/40">
              Standalone calendar: its future direct entries are cancelled.
            </p>
          ) : null}
        </div>
        <DialogFooter className="flex flex-row items-center justify-end gap-2 border-t border-border/60 bg-background px-6 py-4 sm:justify-end">
          <Button type="button" variant="outline" disabled={deleting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={busy}
            onClick={() => {
              void onConfirm?.();
            }}
          >
            {deleting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> : null}
            {deleting ? 'Deleting…' : 'Delete calendar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
